import { TextureData, TextureStatesData } from "../../types/types";
import { ButtonEvents } from "../../ui/ButtonBase";
import { IconButton } from "../../ui/IconButton";
import { LoaderFileKeys } from "../constant/AppConstants";
import { AppSettings } from "../managers/AppSettings";


export enum ChoiceSelectorEvents {
    /**
     * Value change event
     */
    CHANGE = "change",
}

/**
 * Left and Right button and bg and value textfield,
 * where the textfield value comes from choice array, that you can traverse by using L/R buttons
 * - clamping on L/R indexes (or disableIndexes which cant be traversed)
 */
export class ChoiceSelector 
    extends Phaser.GameObjects.Container
{
    // public bg: Phaser.GameObjects.Image;
    public bg: Phaser.GameObjects.NineSlice;
    public btnLeft: IconButton;
    public btnRight: IconButton;
    public txtValue: Phaser.GameObjects.Text;

    protected choices: string[];
    // protected maxValueTextWidth: number = 225;
    protected curChoiceIndex: number;
    /**
     * minimum index from which you can scroll u pto max index
     */
    protected startChoiceIndex: number;
    /**
     * 
     */
    protected bgTexturesData: TextureData;
    protected btnLeftTexturesData: TextureStatesData;
    protected btnRightTexturesData: TextureStatesData;
    protected eventEmitter: Phaser.Events.EventEmitter;


    public Init(
        bgTexturesData: TextureData,
        btnLeftTexturesData: TextureStatesData,
        btnRightTexturesData: TextureStatesData,
    ): void
    {
        this.bgTexturesData = bgTexturesData;
        this.btnLeftTexturesData = btnLeftTexturesData;
        this.btnRightTexturesData = btnRightTexturesData;

        this.choices = [];
        this.curChoiceIndex = 0;
        this.startChoiceIndex = 0;
        this.eventEmitter = new Phaser.Events.EventEmitter();
    }

    public Build(): void
    {
        this.bg = this.scene.make.nineslice({
            key: this.bgTexturesData.texture,
            frame: this.bgTexturesData.frame,
            leftWidth: 64,
            rightWidth: 64,
        });
        this.bg.width = this.scene.textures.getFrame(
            this.bgTexturesData.texture, 
            this.bgTexturesData.frame,
        ).width;
        this.add(this.bg);

        // spacing from bg edge to button edge (one side)
        // const centerY: number = this.bg.displayHeight/2;
        // const bgHalfWidth: number = this.bg.displayWidth/2;

        // btnLeft
        this.btnLeft = new IconButton(this.scene);
        this.btnLeft.InitBase(this.btnLeftTexturesData);
        this.btnLeft.InitIcon({
            out:{texture:LoaderFileKeys.ui, frame:"icon-minus.png"},
        });
        this.btnLeft.PostInit(20);
        // const btnHalfWidth: number = this.btnLeft.displayWidth/2;
        // const btnMargin: number = centerY - this.btnLeft.displayHeight/2;
        // this.btnLeft.setPosition(- bgHalfWidth + btnMargin + btnHalfWidth, 0);
        // shadow
        const btnLeftShadow = this.scene.make.image({key:LoaderFileKeys.ui, frame:"circle-01-shadow.png"}).setScale(0.70);
        this.btnLeft.add(btnLeftShadow);
        this.btnLeft.sendToBack(btnLeftShadow);
        this.add(this.btnLeft);
        
        // btnRight
        this.btnRight = new IconButton(this.scene/*, bgHalfWidth - btnHalfWidth - btnMargin, 0*/);
        this.btnRight.InitBase(this.btnRightTexturesData);
        this.btnRight.InitIcon({
            out:{texture:LoaderFileKeys.ui, frame:"icon-plus.png"},
        });
        this.btnRight.PostInit(20);
        // shadow
        const btnRightShadow = this.scene.make.image({key:LoaderFileKeys.ui, frame:"circle-01-shadow.png"}).setScale(0.70);
        this.btnRight.add(btnRightShadow);
        this.btnRight.sendToBack(btnRightShadow);
        this.add(this.btnRight);
        
        // txtValue
        this.txtValue = this.scene.make.text({
            style: {
                color: "#000",
                fontSize: "60px",
                align: "center",
                fontFamily: AppSettings.fontFamilies.MontserratBold,
                metrics: AppSettings.metrics.montserratBold60,
            }
        }).setOrigin(0.5);
        this.add(this.txtValue);
    }

    /**
     * Set size of this component
     * NOTE: component bg has no effects, so set size equals bg displayWidth/displayHeight; inner content aligns based on that.
     */
    public SetWidth(width: number): void
    {
        this.bg.width = width;
        // this.txtValue.x = this.bg.width * 0.5;
        // console.trace(width, this.bg.width);

        // update scale from new width
        this.UpdateView();
    }

    /**
     * NOTE: set this before setting @see SetChoiceIndex or @see SetStartChoiceIndex
     * @param choices 
     */
    public SetChoices(choices: string[]): void
    {
        this.choices = choices;
        this.UpdateView();
    }
    
    public GetChoices(): string[]
    {
        return this.choices;
    }

    /**
     * Update (and scale to fit) label text
     */
    protected UpdateView(): void
    {
        this.txtValue.text = this.choices[this.curChoiceIndex];
        const spaceForText: number = this.bg.width - 265;
        this.txtValue.scale = this.txtValue.width > spaceForText ? 
        spaceForText / this.txtValue.width : 1;

        const bgHalfWidth: number = this.bg.displayWidth/2;
        const centerY: number = this.bg.displayHeight/2;
        const btnMargin: number = centerY - this.btnLeft.displayHeight/2;
        const btnHalfWidth: number = this.btnLeft.displayWidth/2;
        this.btnLeft.x = -bgHalfWidth + btnMargin + btnHalfWidth;
        this.btnRight.x = bgHalfWidth - btnMargin - btnHalfWidth;
    }

    public SetStartChoiceIndex(index: number): void
    {
        // clamp
        index = Phaser.Math.Clamp(index, 0, this.choices.length-1);
        this.startChoiceIndex = index;
        // bump curChoiceIndex, if now on inaccessible index
        if (this.curChoiceIndex < index) {
            this.curChoiceIndex = index;
            this.UpdateView();
        }
        this.UpdateInteractive();
    }

    /**
     * NOTE: Use force=true when changing localizations and setting new value etc.
     * @param index 
     * @param force 
     */
    public SetChoiceIndex(index: number, force: boolean = false, emitChange: boolean = false): void
    {
        // clamp to choices array
        index = Phaser.Math.Clamp(index, 0, this.choices.length-1);
        
        // update view
        // NOTE: if not already set (except this might need to override every time, 
        //       if we use this control with different localizations etc); use force=true;
        if ((index != this.curChoiceIndex) || force) {
            // NOTE: setting choiceIndex can push startChoiceIndex to the left! 
            //       (choiceIndex has higher priority)
            if (index < this.startChoiceIndex) {
                this.startChoiceIndex = index;
            }
            // cache data
            this.curChoiceIndex = index;
            // update view
            this.UpdateView();
            // emit value change event
            if (emitChange) {
                this.eventEmitter.emit(ChoiceSelectorEvents.CHANGE, this.curChoiceIndex, true);
            }
        }
        // udpate interactivity of buttons based on curChoiceIndex
        this.UpdateInteractive();
    }

    public GetValueIndex(): number
    {
        return this.curChoiceIndex;
    }

    protected RegisterListeners(): void
    {
        this.btnLeft.on(ButtonEvents.CLICK, this.OnBtnLeftClick, this);
        this.btnRight.on(ButtonEvents.CLICK, this.OnBtnRightClick, this);
    }

    protected UnregisterListeners(): void
    {
        this.btnLeft.off(ButtonEvents.CLICK, this.OnBtnLeftClick);
        this.btnRight.off(ButtonEvents.CLICK, this.OnBtnRightClick);
    }

    protected UpdateInteractive(): void
    {
        this.btnLeft.SetInteractive(this.curChoiceIndex != this.startChoiceIndex);
        this.btnRight.SetInteractive(this.curChoiceIndex != this.choices.length - 1);
    }

    /**
     * Sets custom interactive for this component
     * true = updates interactive of children based on curChoiceIndex
     * false = disables interactiveo n all children
     * 
     * @param value 
     */
    public SetInteractive(value: boolean): void
    {
        // console.log("SetInteractive",this.name,value);

        if (value) {
            this.UpdateInteractive();
            this.RegisterListeners();
        }
        else {
            this.btnLeft.SetInteractive(false);
            this.btnRight.SetInteractive(false);
            this.UnregisterListeners();
        }
    }

    public GetEventEmitter(): Phaser.Events.EventEmitter
    {
        return this.eventEmitter;
    }

    protected OnBtnLeftClick(): void
    {
        this.SetChoiceIndex(this.curChoiceIndex - 1, false, true); // force? emitChange?
    }

    protected OnBtnRightClick(): void
    {
        this.SetChoiceIndex(this.curChoiceIndex + 1, false, true); // force? emitChange?
    }
}